import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight,
  Users,
  Globe,
  Shield,
  ArrowRightLeft,
  Check,
  Smartphone,
  Star,
  Percent,
  Headphones,
  Download,
  Clock,
  Lock,
  ChevronRight,
  Store,
  Banknote,
  ArrowUpRight,
  WalletCards,
  ArrowLeftRight,
  ShieldCheck,
} from "lucide-react";

import { SiX, SiInstagram, SiYoutube } from "react-icons/si";
import { FaLinkedinIn } from "react-icons/fa";
import { useMarketData } from "../hooks/useMarketData";
import { cn } from "@/lib/utils";
import Converter from "@/components/Converter";
import { Logo } from "@/components/Logo";
import PhoneMockUp from "@/components/phone-mockup/PhoneMockUp";
import BuyFiatTestimonials from "@/components/BuyFiatTestimonials";

interface SupportedCountry {
  name: string;
  token: string;
  currency: string;
  countryCode: string;
  status: "Live" | "Coming soon";
  payments: string[];
}

gsap.registerPlugin(ScrollTrigger);

const LandingPage = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const countriesRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const socialLinks = [
    {
      icon: SiX,
      url: "https://x.com/buyfiat_app",
      label: "X",
    },
    {
      icon: FaLinkedinIn,
      url: "https://www.linkedin.com/company/buyfiat-app/",
      label: "LinkedIn",
    },
    {
      icon: SiInstagram,
      url: "https://www.instagram.com/buyfiat.app?stkn=cWduaGNqODBiMXh1",
      label: "Instagram",
    },
    {
      icon: SiYoutube,
      url: "#",
      label: "YouTube",
    },
  ];

  const features = [
    {
      icon: ArrowRightLeft,
      title: "Instant Conversion",
      desc: "Convert between any African currency in seconds with real-time rates",
      color: "#F0B90B",
    },
    {
      icon: Users,
      title: "P2P Marketplace",
      desc: "Trade directly with verified users across Africa with escrow protection",
      color: "#E07B39",
    },
    {
      icon: Shield,
      title: "Smart Escrow",
      desc: "Blockchain-secured transactions with AI-powered dispute resolution",
      color: "#0ECB81",
    },
    {
      icon: Smartphone,
      title: "Mobile Money",
      desc: "Integrated with M-Pesa, MTN MoMo, OPay, and all major providers",
      color: "#1E80FF",
    },
    {
      icon: Percent,
      title: "Low Fees",
      desc: "Just 0.1% trading fee. No hidden charges. Transparent pricing always.",
      color: "#F0B90B",
    },
    {
      icon: Headphones,
      title: "24/7 Support",
      desc: "Local support teams in every country speaking your language",
      color: "#E07B39",
    },
  ];

  const countries: SupportedCountry[] = [
    {
      name: "Nigeria",
      token: "NGNT",
      currency: "Nigerian Naira",
      countryCode: "ng",
      status: "Live",
      payments: ["Bank Transfer", "USSD", "OPay", "PalmPay"],
    },
    {
      name: "Ghana",
      token: "GHST",
      currency: "Ghanaian Cedi",
      countryCode: "gh",
      status: "Live",
      payments: ["MTN MoMo", "Telecel Cash", "AirtelTigo"],
    },
    {
      name: "Kenya",
      token: "KENT",
      currency: "Kenyan Shilling",
      countryCode: "ke",
      status: "Live",
      payments: ["M-Pesa", "Airtel Money", "Bank Transfer"],
    },
    {
      name: "South Africa",
      token: "ZART",
      currency: "South African Rand",
      countryCode: "za",
      status: "Coming soon",
      payments: ["Instant EFT", "Bank Transfer"],
    },
    {
      name: "Egypt",
      token: "EGPT",
      currency: "Egyptian Pound",
      countryCode: "eg",
      status: "Coming soon",
      payments: ["Fawry", "Mobile Wallets", "Bank Transfer"],
    },
  ];

  const steps = [
    {
      number: "01",
      title: "Create Account",
      desc: "Sign up with email and phone. Verify in minutes with our streamlined KYC process.",
    },
    {
      number: "02",
      title: "Verify Identity",
      desc: "Complete KYC to unlock full trading. AI-powered verification in under 5 minutes.",
    },
    {
      number: "03",
      title: "Deposit Funds",
      desc: "Add money via bank, mobile money, or crypto. Multiple options available.",
    },
    {
      number: "04",
      title: "Start Trading",
      desc: "Buy, sell, and convert across Africa. Trade 24/7 with instant settlements.",
    },
  ];

  const testimonials = [
    {
      name: "Adeola Johnson",
      role: "Trader, Lagos",
      country: "🇳🇬",
      text: "BUYFIAT has transformed how I trade crypto. The instant settlements and low fees are game-changers for my business.",
      rating: 5,
    },
    {
      name: "Kwame Asante",
      role: "Business Owner, Accra",
      country: "🇬🇭",
      text: "The P2P marketplace is incredibly secure. I have traded over $50,000 without a single issue. Highly recommended!",
      rating: 5,
    },
    {
      name: "Sarah Kimani",
      role: "Freelancer, Nairobi",
      country: "🇰🇪",
      text: "M-Pesa integration makes deposits and withdrawals seamless. Best crypto platform for East Africa!",
      rating: 5,
    },
    {
      name: "Pieter van der Merwe",
      role: "Investor, Cape Town",
      country: "🇿🇦",
      text: "Professional platform with excellent customer support. The analytics tools help me make informed decisions.",
      rating: 5,
    },
  ];

  const partners = [
    "Chainalysis",
    "Fireblocks",
    "Sumsub",
    "Onfido",
    "Circle",
    "Paxos",
  ];

  const products = [
    {
      name: "P2P Market",
      url: "/p2p",
    },
    {
      name: "Forex Exchange",
      url: "/forex",
    },
  ];

  const companies = [
    {
      name: "About Us",
      url: "#",
    },
    {
      name: "Terms",
      url: "/terms",
    },
    {
      name: "Privacy",
      url: "/privacy",
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hero-title",
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out" },
      );
      gsap.fromTo(
        ".hero-subtitle",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, delay: 0.2, ease: "power3.out" },
      );
      gsap.fromTo(
        ".hero-cta",
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 0.6, delay: 0.4, ease: "power3.out" },
      );
      gsap.fromTo(
        ".token-orbit-item",
        { opacity: 0, scale: 0 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          stagger: 0.1,
          delay: 0.6,
          ease: "back.out",
        },
      );

      gsap.fromTo(
        ".stat-item",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          scrollTrigger: { trigger: statsRef.current, start: "top 85%" },
        },
      );

      gsap.fromTo(
        ".feature-card",
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          scrollTrigger: { trigger: featuresRef.current, start: "top 80%" },
        },
      );

      gsap.fromTo(
        ".country-card",
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
          scrollTrigger: { trigger: countriesRef.current, start: "top 80%" },
        },
      );

      gsap.fromTo(
        ".testimonial-card",
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.15,
          scrollTrigger: { trigger: testimonialsRef.current, start: "top 80%" },
        },
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div
      ref={heroRef}
      className="relative mt-10 sm:mt-0 overflow-hidden bg-background"
    >
      {/* Hero Section */}

      <div className="flex flex-col gap-5">
        <section className="relative overflow-hidden bg-background">
          {/* Background animation layers (same as before) */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div
              className="absolute inset-0 opacity-30 dark:opacity-25"
              style={{
                background:
                  "radial-gradient(ellipse 90% 55% at 50% -15%, rgba(240,185,11,0.25), transparent 70%)",
              }}
            />
            <div className="absolute inset-0">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-primary/40 dark:bg-primary/50"
                  style={{
                    width: `${5 + (i % 3)}px`,
                    height: `${5 + (i % 3)}px`,
                    left: `${5 + i * 8}%`,
                    top: `${12 + (i % 5) * 15}%`,
                    animation: `buyfiat-float ${6 + (i % 4)}s ease-in-out infinite`,
                    animationDelay: `${i * 0.4}s`,
                  }}
                />
              ))}
            </div>
            <div className="absolute inset-0">
              {[...Array(5)].map((_, i) => (
                <div
                  key={`flow-${i}`}
                  className="absolute h-[1px] bg-gradient-to-r from-transparent via-primary/60 to-transparent"
                  style={{
                    top: `${20 + i * 16}%`,
                    left: 0,
                    width: "100%",
                    animation: `buyfiat-flow ${9 + i * 1.5}s linear infinite`,
                    animationDelay: `${i * 1.2}s`,
                  }}
                />
              ))}
            </div>
            <div
              className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
              style={{
                backgroundImage: `linear-gradient(rgba(240,185,11,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(240,185,11,0.6) 1px, transparent 1px)`,
                backgroundSize: "48px 48px",
              }}
            />
          </div>

          {/* Main content + Image */}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-14 md:pt-24 md:pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Left – Text content */}
              <div className="max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
                <div className="inline-flex items-center justify-center gap-2.5 px-3.5 py-1.5 rounded-full bg-muted border border-border text-xs font-medium text-muted-foreground mb-6">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  Live currency marketplace
                </div>

                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground tracking-tight leading-[1.1]">
                  Find. <span className="text-primary">Compare.</span>{" "}
                  <span className="bg-gradient-to-r from-primary via-[#fcd535] to-primary bg-clip-text text-transparent">
                    Exchange.
                  </span>
                </h1>

                <p className="mt-6 text-lg sm:text-xl text-foreground/90 leading-relaxed">
                  Your gateway to currency liquidity across Africa and beyond.
                </p>

                <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Connect with verified currency merchants, compare live rates,
                  and let BuyFiat securely facilitate your transaction from
                  start to finish.
                </p>

                <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-3">
                  <Link
                    to="/convert"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm sm:text-base shadow-md shadow-primary/20 hover:opacity-90 transition-all duration-200"
                  >
                    Find Currency
                    <ArrowRight size={18} />
                  </Link>

                  <Link
                    to="/dashboard/p2p"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-muted border border-border text-foreground font-semibold text-sm sm:text-base hover:border-primary/50 transition-all duration-200"
                  >
                    <Store size={18} />
                    Become a Merchant
                  </Link>
                </div>
              </div>

              {/* Right – High-resolution image */}
              <div className="relative w-full max-w-lg mx-auto lg:max-w-none">
                <PhoneMockUp />
              </div>
            </div>
            <div className="relative z-10 grid grid-cols-2 border-t border-border bg-background/50 sm:grid-cols-4">
              {[
                {
                  icon: ShieldCheck,
                  title: "Secure",
                  description: "Protected funds",
                },
                {
                  icon: ArrowLeftRight,
                  title: "P2P Exchange",
                  description: "Trade directly",
                },
                {
                  icon: WalletCards,
                  title: "Multi-asset",
                  description: "One wallet",
                },
                {
                  icon: ArrowUpRight,
                  title: "Fast transfers",
                  description: "Move money easily",
                },
              ].map(({ icon: Icon, title, description }, index) => (
                <div
                  key={title}
                  className={`flex items-center gap-3 border-border px-4 py-3.5 sm:px-5 ${
                    index > 1 ? "border-t sm:border-t-0" : ""
                  } ${index % 2 === 1 ? "border-l" : ""} ${
                    index === 2 ? "sm:border-l" : ""
                  }`}
                >
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10">
                    <Icon className="size-5 text-primary" />
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-foreground">
                      {title}
                    </p>

                    <p className="truncate text-xs text-muted-foreground">
                      {description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Bar */}
          <div className="relative border-y border-border bg-muted/50 dark:bg-muted/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-12">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-6">
                {[
                  {
                    value: "12",
                    label: "Supported Currencies",
                    icon: Banknote,
                    sublabel: "Live & expanding",
                  },
                  {
                    value: "4",
                    label: "Active Merchants",
                    icon: Users,
                    sublabel: "Across Africa",
                  },
                  {
                    value: "5",
                    label: "African Countries",
                    icon: Globe,
                    sublabel: "And growing",
                  },
                  {
                    value: "0.1%",
                    label: "Trading Fee",
                    icon: Percent,
                    sublabel: "Lowest in Africa",
                  },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="flex flex-col items-center text-center group"
                  >
                    <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary mb-3 group-hover:scale-110 transition-transform duration-300" />
                    <div className="text-2xl sm:text-3xl font-bold text-foreground font-mono tracking-tight">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-sm font-medium text-foreground">
                      {stat.label}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {stat.sublabel}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Keyframes */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
        @keyframes buyfiat-float {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.4; }
          50% { transform: translateY(-32px) scale(1.25); opacity: 0.85; }
        }
        @keyframes buyfiat-flow {
          0% { transform: translateX(-100%); opacity: 0; }
          20% { opacity: 0.7; }
          80% { opacity: 0.6; }
          100% { transform: translateX(100%); opacity: 0; }
        }
      `,
            }}
          />
        </section>
      </div>

      <div className="lg:pl-4 w-full mt-40">
        <Converter />
      </div>

      {/* <DashboardHero
        firstName={"Your way"}
        currency={"NGN"}
        totalBalance={999}
        assets={[]}
        kycVerified={true}
      /> */}

      {/* Features Section */}
      <section ref={featuresRef} className="py-24">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-16">
            <span className="text-primary text-sm font-medium mb-4 block">
              FEATURES
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-foreground">
              Everything You Need to <span className="text-primary">Trade</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              A complete trading platform designed specifically for African
              markets
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="feature-card nex-card p-6 hover:border-primary/50 transition-all duration-300 group cursor-pointer"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon
                    className="w-6 h-6"
                    style={{ color: feature.color }}
                  />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground">{feature.desc}</p>
                <div className="mt-4 flex items-center gap-2 text-primary text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Countries Section */}

      <section
        ref={countriesRef}
        aria-labelledby="supported-markets-heading"
        className="relative overflow-hidden bg-muted/30 py-16 sm:py-20 lg:py-24"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -right-32 -top-32 h-80 w-80 rounded-full bg-primary/[0.06] blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-primary/[0.05] blur-3xl" />
        </div>

        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section heading */}
          <div className="mx-auto mb-10 max-w-2xl text-center sm:mb-14 lg:mb-16">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                Our African markets
              </span>
            </div>

            <h2
              id="supported-markets-heading"
              className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl"
            >
              Built for Africa.
              <span className="mt-1 block text-primary">
                Designed for growth.
              </span>
            </h2>

            <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-muted-foreground sm:text-base">
              Explore the local currencies and payment ecosystems BUYFIAT is
              building around. Our market coverage will expand as support
              becomes available.
            </p>
          </div>

          {/* Market summary */}
          <div className="mx-auto mb-8 grid max-w-3xl grid-cols-2 gap-3 sm:mb-10 sm:grid-cols-3 sm:gap-4">
            <div className="rounded-2xl border border-border bg-card/80 p-4 text-center shadow-sm sm:p-5">
              <p className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                {countries.length}
              </p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                Featured markets
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card/80 p-4 text-center shadow-sm sm:p-5">
              <p className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                {
                  countries.filter((country) => country.status === "Live")
                    .length
                }
              </p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                Live markets
              </p>
            </div>

            <div className="col-span-2 rounded-2xl border border-border bg-card/80 p-4 text-center shadow-sm sm:col-span-1 sm:p-5">
              <p className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
                Local
              </p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                Market-focused payments
              </p>
            </div>
          </div>

          {/* Country cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {countries.map((country) => {
              const isLive = country.status === "Live";

              return (
                <article
                  key={country.countryCode}
                  className="group relative flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg sm:p-6"
                >
                  {/* Country heading */}
                  <div className="mb-5 flex items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
                      <img
                        src={`https://flagcdn.com/w80/${country.countryCode}.png`}
                        alt={`${country.name} flag`}
                        width={48}
                        height={48}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                    </div>

                    <div className="min-w-0 flex-1 pt-0.5">
                      <h3 className="break-words text-base font-bold text-foreground">
                        {country.name}
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {country.currency}
                      </p>
                    </div>
                  </div>

                  {/* Market status */}
                  <div className="mb-5 flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Market status
                    </span>

                    <span
                      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                        isLive
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          isLive ? "bg-emerald-500" : "bg-muted-foreground"
                        }`}
                      />
                      {country.status}
                    </span>
                  </div>

                  {/* Currency information */}
                  <div className="mb-5 rounded-xl border border-border bg-muted/30 p-3.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Local asset
                    </p>
                    <p className="mt-1.5 break-all font-mono text-lg font-bold text-foreground">
                      {country.token}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      {isLive
                        ? "Check current availability in the platform."
                        : "Market support is being developed."}
                    </p>
                  </div>

                  {/* Payment methods */}
                  <div className="mt-auto border-t border-border pt-4">
                    <p className="mb-3 text-xs font-semibold text-foreground">
                      Payment ecosystem
                    </p>

                    <div className="flex flex-wrap gap-1.5">
                      {country.payments.map((payment) => (
                        <span
                          key={payment}
                          className="max-w-full break-words rounded-md border border-border bg-background px-2 py-1.5 text-[10px] leading-4 text-muted-foreground sm:text-[11px]"
                        >
                          {payment}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
                </article>
              );
            })}
          </div>

          {/* Closing statement */}
          <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-primary/15 bg-primary/[0.04] p-5 sm:mt-10 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground">
                Growing market by market
              </h3>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Our focus is reliable local payment access, transparent pricing,
                and a consistent experience across supported markets.
              </p>
            </div>

            <span className="inline-flex w-fit shrink-0 items-center gap-2 rounded-lg border border-primary/20 bg-card px-3 py-2 text-xs font-semibold text-primary">
              <span className="h-2 w-2 rounded-full bg-primary" />
              Expanding across Africa
            </span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-16">
            <span className="text-primary text-sm font-medium mb-4 block">
              GET STARTED
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-foreground">
              How It <span className="text-primary">Works</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Get started in minutes with our simple 4-step process
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative group">
                <div className="nex-card p-6 h-full hover:border-primary/30 transition-colors">
                  <div className="text-4xl font-bold text-primary/30 mb-4 font-mono group-hover:text-primary/50 transition-colors">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px bg-border" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <BuyFiatTestimonials />

      {/* Partners */}
      <section className="py-16 border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <p className="text-center text-muted-foreground text-sm mb-8">
            Trusted by industry leaders
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {partners.map((partner) => (
              <div
                key={partner}
                className="text-muted-foreground/60 hover:text-muted-foreground transition-colors text-lg font-semibold"
              >
                {partner}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-primary text-sm font-medium mb-4 block">
                SECURITY
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-foreground">
                Your Assets Are <span className="text-primary">Protected</span>
              </h2>
              <p className="text-muted-foreground mb-8">
                We employ industry-leading security measures to ensure your
                funds and data are always safe.
              </p>
              <div className="space-y-4">
                {[
                  {
                    icon: Lock,
                    title: "Cold Storage",
                    desc: "95% of assets stored offline",
                  },
                  {
                    icon: Shield,
                    title: "Insurance Coverage",
                    desc: "Assets insured up to $100M",
                  },
                  {
                    icon: Clock,
                    title: "24/7 Monitoring",
                    desc: "Security team always on watch",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-foreground font-medium">
                        {item.title}
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl blur-3xl" />
              <div className="relative nex-card p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-muted-foreground">Security Score</span>
                  <span className="text-emerald-500 font-bold">A+</span>
                </div>
                <div className="space-y-4">
                  {[
                    { label: "Encryption", value: 100 },
                    { label: "2FA Adoption", value: 94 },
                    { label: "Cold Storage", value: 95 },
                    { label: "Compliance", value: 100 },
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">
                          {item.label}
                        </span>
                        <span className="text-foreground">{item.value}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full"
                          style={{ width: `${item.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 lg:px-6">
          <div className="nex-card p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />

            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Logo
                  iconOnly
                  size="xl"
                  className="transition-transform duration-300"
                />
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-foreground">
                Ready to Start <span className="text-primary">Trading?</span>
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                Join millions of traders across Africa. Create your free account
                in minutes.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <Link
                  to="/register"
                  className="btn-gold flex items-center gap-2 text-lg px-8 py-4 group"
                >
                  Create Account
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="btn-outline flex items-center gap-2 text-lg px-8 py-4">
                  <Download className="w-5 h-5" />
                  Download App
                </button>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                {[
                  "Zero setup fees",
                  "0.1% trading fee",
                  "Instant settlements",
                  "24/7 support",
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <Check className="w-4 h-4 text-emerald-500" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
            <div className="lg:col-span-2">
              <Logo size="xl" className="transition-transform duration-300" />

              <p className="text-muted-foreground text-sm mb-4 max-w-sm">
                Africa's premier cryptocurrency exchange. Trade crypto with
                African currency tokens across the continent.
              </p>
              <div className="flex gap-4">
                {socialLinks.map((link) => {
                  const Icon = link.icon;

                  return (
                    <a
                      key={link.label}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.label}
                      className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="text-foreground font-semibold mb-4">Products</h4>
              <ul className="space-y-2">
                {products.map((item, i) => (
                  <li key={i}>
                    <Link
                      to={item?.url}
                      className="text-muted-foreground hover:text-primary text-sm transition-colors"
                    >
                      {item?.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-foreground font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                {[
                  "Help Center",
                  // "API Docs",
                  // "Trading Fees",
                  "Verification",
                ].map((item, i) => (
                  <li key={i}>
                    <Link
                      to="/help"
                      className="text-muted-foreground hover:text-primary text-sm transition-colors"
                    >
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-foreground font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                {companies?.map((item, i) => (
                  <li key={i}>
                    <Link
                      to={item.url}
                      className="text-muted-foreground hover:text-primary text-sm transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground/60 text-sm">
              © 2024 BUYFIAT. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/terms"
                className="text-muted-foreground/60 hover:text-muted-foreground text-sm transition-colors"
              >
                Terms
              </Link>
              <Link
                to="/privacy"
                className="text-muted-foreground/60 hover:text-muted-foreground text-sm transition-colors"
              >
                Privacy
              </Link>
              <Link
                to="/help"
                className="text-muted-foreground/60 hover:text-muted-foreground text-sm transition-colors"
              >
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
